'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { ArrowDown, ArrowUp, LogOut } from 'lucide-react';
import { strobeOnHover } from '@/components/ui/nav-strobe';
import { useFooter } from '@/contexts/FooterContext';
import { useAuth } from '@/contexts/AuthContext';
import { COLLECTOR_ROUTES } from '@/lib/constants/collector';
import { features } from '@/lib/config/features';
import { toggleAboutSeries, ABOUT_SERIES_STATE_EVENT } from '@/components/ui/catalog/collection/about-series-reveal';

interface SubInfoProps {
  pageKey?: string;
  buttons?: Array<{
    title: string;
    url: string;
    active: boolean;
  }>;
}

interface BottomNavbarProps {
  subInfo?: SubInfoProps;
  inverted?: boolean;
}

interface BottomNavButtonProps {
  href?: string;
  className?: string;
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  title?: string;
}

// Component for bottom navbar buttons with strobing effect
function BottomNavButton({ href, className, children, onClick, title }: BottomNavButtonProps) {
  return (
    <div>
      {href ? (
        <Link
          href={href}
          className={`nav-button nav-strobe inline-flex items-center ${className || ''}`}
          title={title}
          onClick={onClick}
          {...strobeOnHover}
        >
          {children}
        </Link>
      ) : (
        <button title={title} onClick={onClick} className="align-middle nav-strobe" {...strobeOnHover}>
          {children}
        </button>
      )}
    </div>
  );
}

export function BottomNavbar({ subInfo = {}, inverted = false }: BottomNavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  /* The series reveal broadcasts its open state so this button can read
     "close" while it is open. */
  const [aboutSeriesOpen, setAboutSeriesOpen] = useState(false);
  useEffect(() => {
    const onState = (e: Event) => setAboutSeriesOpen(Boolean((e as CustomEvent).detail?.open));
    window.addEventListener(ABOUT_SERIES_STATE_EVENT, onState);
    return () => window.removeEventListener(ABOUT_SERIES_STATE_EVENT, onState);
  }, []);
  const { footerMode, isFooterOpen, toggleFooter } = useFooter();
  const { isLoggedIn, isHydrated, logout } = useAuth();

  // Track scroll position for 'scroll' mode
  // Content scrolls inside .fixedlayout, not the window
  useEffect(() => {
    if (footerMode !== 'scroll') return undefined;

    const scrollContainer = document.querySelector('.fixedlayout') as HTMLElement | null;
    if (!scrollContainer) return undefined;

    const handleScroll = () => {
      setIsScrolled(scrollContainer.scrollTop > 300);
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [footerMode]);

  const handleScrollClick = () => {
    if (footerMode === 'toggle') {
      toggleFooter();
      return;
    }

    // 'scroll' mode — scroll the .fixedlayout container, not window
    const scrollContainer = document.querySelector('.fixedlayout') as HTMLElement | null;
    if (!scrollContainer) return;

    if (isScrolled) {
      scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      scrollContainer.scrollTo({ top: scrollContainer.scrollHeight, behavior: 'smooth' });
    }
  };

  if (footerMode === 'hidden') return null;

  // Scroll mode puts this control inline above the footer. Keep it fixed only
  // when the footer is an overlay (toggle), or the overlay cannot be opened.
  const showUpdatesHere = footerMode === 'toggle';
  const hasAddons = subInfo.pageKey === 'collection-details' && (subInfo.buttons?.length || 0) > 0;
  const hasAccount = features.stage;
  if (!showUpdatesHere && !hasAddons && !hasAccount) {
    return null;
  }

  // Determine button state based on mode
  const isActive = footerMode === 'toggle' ? isFooterOpen : isScrolled;
  const collectorDashboardHref = COLLECTOR_ROUTES.DASHBOARD;

  return (
    <div className={`fixed bottom-0 left-0 right-0 z-40 shadow-md hidden lg:block px-2 pt-0 pb-2 main-bottom-navbar ${inverted ? 'bg-white text-black' : 'bg-black text-white'}`}>
      <div className="flex justify-between items-center">
        <div className="flex-1 flex">
          {showUpdatesHere && (
          <div>
            <button
              onClick={handleScrollClick}
              className="nav-button nav-strobe inline-flex items-center"
              {...strobeOnHover}
            >
              <span>
                {isActive
                  ? 'SCROLL UP'
                  : footerMode === 'toggle'
                    ? 'SEE RECENT UPDATES'
                    : 'SCROLL DOWN TO SEE RECENT UPDATES'}
              </span>
              {isActive ? (
                <ArrowUp size={16} className="ml-1" />
              ) : (
                <ArrowDown size={16} className="ml-1" />
              )}
            </button>
          </div>
          )}
        </div>

        <div className="flex-1 flex items-center justify-center gap-x-4" id="addon-nav">
          {subInfo.pageKey === 'collection-details' && (
            <>
              {subInfo.buttons?.map((button, index) => (
                <BottomNavButton
                  key={index}
                  href={button.url}
                  className={`${button.active ? ' text-gray-300' : ''}`}
                  onClick={
                    button.url?.startsWith('#')
                      ? e => {
                          /*
                           * In-page reveal: the artworks live in their own
                           * scroll container, so drive the scroll explicitly
                           * rather than relying on default anchor behaviour.
                           */
                          e.preventDefault();
                          // Same button opens and closes the reveal.
                          toggleAboutSeries();
                        }
                      : undefined
                  }
                >
                  <span>
                    {button.url?.startsWith('#')
                      ? aboutSeriesOpen
                        ? 'close \u00d7'
                        : `${button.title} \u2193`
                      : button.title}
                  </span>
                </BottomNavButton>
              ))}
            </>
          )}
        </div>

        <div className="flex-1 flex items-center justify-end gap-x-2">
          {features.stage && (
            <>
              {isHydrated && isLoggedIn ? (
                <>
                  <BottomNavButton href={collectorDashboardHref}>
                    <span>MY ACCOUNT</span>
                  </BottomNavButton>
                  <BottomNavButton onClick={() => logout()} title="Sign Out">
                    <LogOut size={14} />
                  </BottomNavButton>
                </>
              ) : (
                <>
                  <BottomNavButton
                    href={collectorDashboardHref}
                  >
                    <span>COLLECTOR ACCESS</span>
                  </BottomNavButton>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
